import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, ArrowLeft, RefreshCw, AlertCircle, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function EmailConfirmation() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [diagnostics, setDiagnostics] = useState<string[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    // Log de diagnóstico
    const logs = [
      `📧 Email: ${email || 'Não fornecido'}`,
      `🌐 Hostname: ${window.location.hostname}`,
      `🔗 URL atual: ${window.location.href}`,
      `⏰ Timestamp: ${new Date().toISOString()}`,
    ];
    setDiagnostics(logs);
    console.log('📋 Diagnóstico da página de confirmação:', logs);
  }, [email]);

  useEffect(() => {
    // Cooldown timer
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    if (!email || resendCooldown > 0) return;
    
    setIsResending(true);
    console.log('🔄 Reenviando email de confirmação para:', email);
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email
      });

      if (error) {
        console.error('❌ Erro ao reenviar:', error);
        
        // Verificar se é erro de rate limit
        if (error.message.includes('email_send_rate_limit') || 
            error.message.includes('rate limit') ||
            error.message.includes('too many')) {
          toast({
            title: "⏰ Aguarde um momento",
            description: "Por favor, aguarde alguns minutos antes de solicitar outro email. Isso evita sobrecarga no sistema.",
            variant: "destructive",
          });
          setResendCooldown(60); // 1 minuto de cooldown
        } else {
          toast({
            title: "Erro",
            description: error.message || "Erro ao reenviar email. Tente novamente.",
            variant: "destructive",
          });
        }
      } else {
        console.log('✅ Email reenviado com sucesso');
        toast({
          title: "✅ Email reenviado",
          description: "Verifique sua caixa de entrada e spam.",
        });
        setResendCooldown(30); // 30 segundos de cooldown após sucesso
      }
    } catch (error: any) {
      console.error("❌ Erro inesperado ao reenviar:", error);
      toast({
        title: "Erro",
        description: error?.message || "Erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-elegant">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-full">
              <Mail className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Confirme seu E-mail</CardTitle>
          <CardDescription className="text-base">
            Enviamos um link de confirmação para seu e-mail
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Para continuar, verifique sua caixa de entrada e clique no link de confirmação 
              que enviamos para:
            </p>
            
            {email && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-primary">{email}</p>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground">
              Não esqueça de verificar sua caixa de spam caso não encontre o e-mail.
            </p>
          </div>

          {/* Rate Limit Warning */}
          {resendCooldown > 0 && (
            <Alert className="border-amber-500 bg-amber-50">
              <Clock className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Aguarde {resendCooldown}s antes de reenviar novamente.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-3">
            {email && (
              <Button
                onClick={handleResendEmail}
                disabled={isResending || resendCooldown > 0}
                variant="outline"
                className="w-full"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Reenviando...
                  </>
                ) : resendCooldown > 0 ? (
                  <>
                    <Clock className="h-4 w-4 mr-2" />
                    Aguarde {resendCooldown}s
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Reenviar e-mail
                  </>
                )}
              </Button>
            )}
            
            <Link to="/auth" className="w-full">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar ao login
              </Button>
            </Link>
          </div>

          {/* Debug Info - Only in development */}
          {window.location.hostname === 'localhost' && diagnostics.length > 0 && (
            <div className="mt-4 p-3 bg-muted rounded-lg border border-border">
              <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                <AlertCircle className="h-3 w-3" />
                Informações de Diagnóstico (dev)
              </p>
              <div className="space-y-1">
                {diagnostics.map((log, i) => (
                  <p key={i} className="text-xs font-mono text-muted-foreground">{log}</p>
                ))}
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <p className="text-xs text-center text-muted-foreground">
              Problemas com a confirmação? Entre em contato: contato@lyrarh.com.br
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}